import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, Trash2, Pencil, Heart, Play, AlertCircle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import AdminLayout from '../../components/admin/AdminLayout';

const emptyForm = {
  name: '', days_inactive_min: 14, days_inactive_max: 30, credit_amount: 30,
  cooldown_days: 60, channels: ['sms'], message_template: '', enabled: true, priority: 0,
};

const AdminWinback = () => {
  const [rules, setRules] = useState([]);
  const [issuances, setIssuances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [running, setRunning] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const api = (await import('../../utils/api')).default;
      const [r, i] = await Promise.all([
        api.get('/admin/winback-rules'),
        api.get('/admin/winback-issuances?limit=20'),
      ]);
      setRules(r.data.items || []);
      setIssuances(i.data.items || []);
    } catch (err) {
      toast({ title: 'Failed to load', variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setShowDialog(true); };
  const openEdit = (r) => {
    setEditing(r.id);
    setForm({
      name: r.name, days_inactive_min: r.days_inactive_min, days_inactive_max: r.days_inactive_max,
      credit_amount: r.credit_amount, cooldown_days: r.cooldown_days,
      channels: r.channels || ['sms'], message_template: r.message_template || '',
      enabled: r.enabled, priority: r.priority || 0,
    });
    setShowDialog(true);
  };

  const toggleChannel = (ch) => {
    setForm(f => ({
      ...f,
      channels: f.channels.includes(ch) ? f.channels.filter(c => c !== ch) : [...f.channels, ch],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast({ title: 'Name required', variant: 'destructive' }); return; }
    if (form.channels.length === 0) { toast({ title: 'Select at least one channel', variant: 'destructive' }); return; }
    const payload = {
      name: form.name.trim(),
      days_inactive_min: parseInt(form.days_inactive_min, 10),
      days_inactive_max: parseInt(form.days_inactive_max, 10),
      credit_amount: parseFloat(form.credit_amount),
      cooldown_days: parseInt(form.cooldown_days, 10),
      channels: form.channels,
      message_template: form.message_template.trim() || null,
      enabled: form.enabled,
      priority: parseInt(form.priority, 10) || 0,
    };
    try {
      const api = (await import('../../utils/api')).default;
      if (editing) {
        await api.put(`/admin/winback-rules/${editing}`, payload);
        toast({ title: 'Rule updated' });
      } else {
        await api.post('/admin/winback-rules', payload);
        toast({ title: 'Rule created' });
      }
      setShowDialog(false); fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Save failed', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      const api = (await import('../../utils/api')).default;
      await api.delete(`/admin/winback-rules/${id}`);
      toast({ title: 'Deleted' });
      fetchData();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const toggleEnabled = async (r) => {
    try {
      const api = (await import('../../utils/api')).default;
      await api.put(`/admin/winback-rules/${r.id}`, { enabled: !r.enabled });
      fetchData();
    } catch { toast({ title: 'Error', variant: 'destructive' }); }
  };

  const handleRunNow = async () => {
    if (!window.confirm('Run win-back engine now? This will issue credits and send notifications.')) return;
    setRunning(true);
    try {
      const api = (await import('../../utils/api')).default;
      const { data } = await api.post('/admin/winback/run-now');
      toast({ title: 'Win-back complete', description: `${data.credits_issued} credit(s) to ${data.users_targeted} user(s)` });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setRunning(false); }
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="admin-winback-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><Heart className="h-6 w-6 text-pink-600" /> Win-back Credits</h1>
            <p className="text-sm text-gray-500">Auto-issue ride credit + SMS/email to dormant riders. Runs daily at 09:00.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRunNow} disabled={running} data-testid="winback-run-now-btn">
              <Play className="h-4 w-4 mr-1" /> {running ? 'Running...' : 'Run now'}
            </Button>
            <Button onClick={openNew} className="bg-pink-600 hover:bg-pink-700" data-testid="add-winback-rule-btn">
              <Plus className="h-4 w-4 mr-1" /> New Rule
            </Button>
          </div>
        </div>

        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="flex gap-2 items-start">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-medium">How it works</p>
              <p>Each enabled rule scans for riders whose last completed ride was within the inactive window. The same rule won't issue to the same rider again until the cooldown period passes. Credits land in the rider's K-credit wallet and a friendly notification fires via the selected channels.</p>
            </div>
          </div>
        </Card>

        {loading && <p className="text-gray-500">Loading...</p>}

        {!loading && rules.length === 0 && (
          <Card className="p-8 text-center text-gray-500">
            <Heart className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>No win-back rules yet. Create one to bring dormant riders back.</p>
          </Card>
        )}

        <div className="space-y-3" data-testid="winback-rules-list">
          {rules.map(r => (
            <Card key={r.id} className="p-4">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold">{r.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${r.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {r.enabled ? 'Active' : 'Disabled'}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-pink-100 text-pink-700 font-bold">K{r.credit_amount}</span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Inactive {r.days_inactive_min}–{r.days_inactive_max} days · Cooldown {r.cooldown_days}d · Via {(r.channels || []).join(' + ')}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Total issued: {r.totalIssued || 0}{r.lastRunAt ? ` · Last ran ${new Date(r.lastRunAt).toLocaleString()}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={r.enabled} onCheckedChange={() => toggleEnabled(r)} />
                  <Button size="sm" variant="outline" onClick={() => openEdit(r)}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(r.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {issuances.length > 0 && (
          <Card className="p-4">
            <h2 className="font-semibold mb-3">Recent Issuances</h2>
            <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="winback-issuances">
              {issuances.map(iss => (
                <div key={iss.id} className="flex items-center justify-between text-sm py-1.5 border-b last:border-b-0">
                  <div>
                    <span className="font-medium">{iss.ruleName}</span>
                    <span className="text-gray-500 text-xs ml-2">→ user {(iss.userId || '').slice(-6)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-pink-100 text-pink-700 font-medium">K{iss.amount}</span>
                    <span className="text-xs text-gray-500">{(iss.channels_sent || []).join(', ') || '—'}</span>
                    <span className="text-xs text-gray-400">{new Date(iss.issuedAt).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-md" data-testid="winback-rule-dialog">
            <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Win-back Rule</DialogTitle></DialogHeader>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="2-week dormant" data-testid="winback-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Inactive min (days)</Label>
                  <Input type="number" min="1" value={form.days_inactive_min} onChange={e => setForm({ ...form, days_inactive_min: e.target.value })} />
                </div>
                <div>
                  <Label>Inactive max (days)</Label>
                  <Input type="number" min="1" value={form.days_inactive_max} onChange={e => setForm({ ...form, days_inactive_max: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Credit (K)</Label>
                  <Input type="number" step="0.5" min="1" value={form.credit_amount} onChange={e => setForm({ ...form, credit_amount: e.target.value })} />
                </div>
                <div>
                  <Label>Cooldown (days)</Label>
                  <Input type="number" min="7" value={form.cooldown_days} onChange={e => setForm({ ...form, cooldown_days: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Channels</Label>
                <div className="flex gap-2 mt-1">
                  {['sms', 'email', 'whatsapp', 'push'].map(ch => (
                    <button
                      key={ch} type="button" onClick={() => toggleChannel(ch)}
                      className={`flex-1 px-3 py-2 rounded text-xs font-medium border ${form.channels.includes(ch) ? 'bg-pink-600 text-white border-pink-600' : 'bg-white text-gray-700 border-gray-300'}`}
                      data-testid={`winback-channel-${ch}`}
                    >{ch.toUpperCase()}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Message template (use {`{name}`} and {`{amount}`})</Label>
                <textarea
                  value={form.message_template}
                  onChange={e => setForm({ ...form, message_template: e.target.value })}
                  placeholder="Hi {name}! We miss you on Tekstar — here's K{amount} ride credit. Book your next ride and save!"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm h-20"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Priority</Label>
                  <Input type="number" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
                  <Label className="!mb-0">Enabled</Label>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowDialog(false)}>Cancel</Button>
                <Button className="flex-1 bg-pink-600 hover:bg-pink-700" onClick={handleSave} data-testid="winback-save-btn">Save</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminWinback;
